// SAB-431: per-category opt-in modal launched from the consent Banner's
// "Customize" button (and reusable for any "Manage cookie preferences"
// surface in the future). Strictly-necessary is shown but disabled — you
// can't opt out of auth/session cookies.
//
// Dialog from shadcn-ui handles ESC + click-outside + focus-trap a11y.
import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  setConsent,
  getConsent,
  type ConsentCategory,
} from '@/lib/consent'

interface Props {
  open: boolean
  onClose: () => void
}

type Row = {
  key: ConsentCategory | 'necessary'
  label: string
  blurb: string
  locked?: true
}

const ROWS: Row[] = [
  {
    key: 'necessary',
    label: 'Strictly necessary',
    blurb: 'Sign-in, security, fraud prevention. Required to play.',
    locked: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    blurb: "Helps us see what's broken and what people actually use.",
  },
  {
    key: 'marketing',
    label: 'Marketing',
    blurb: 'Lets us tell other people about Sabotage on the platforms they use.',
  },
  {
    key: 'location',
    label: 'Location services',
    blurb: 'Only relevant for state eligibility on real-money play.',
  },
]

export default function CustomizeModal({ open, onClose }: Props) {
  // Seed from current state (if any). Defaults to all-false (opt-in).
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const [location, setLocation] = useState(false)

  useEffect(() => {
    if (!open) return
    const current = getConsent()
    setAnalytics(current?.analytics ?? false)
    setMarketing(current?.marketing ?? false)
    setLocation(current?.location ?? false)
  }, [open])

  const handleSave = () => {
    setConsent({ analytics, marketing, location })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Pick your cookies</DialogTitle>
          <DialogDescription>
            Strictly necessary is on by default (auth, security). Everything else is your call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {ROWS.map(row => {
            const value =
              row.key === 'necessary' ? true
              : row.key === 'analytics' ? analytics
              : row.key === 'marketing' ? marketing
              : location
            const setter =
              row.key === 'analytics' ? setAnalytics
              : row.key === 'marketing' ? setMarketing
              : row.key === 'location' ? setLocation
              : undefined
            return (
              <div
                key={row.key}
                className="flex items-start justify-between gap-4 rounded-md border border-border p-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{row.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{row.blurb}</p>
                </div>
                <Switch
                  checked={value}
                  disabled={row.locked}
                  onCheckedChange={(v) => setter?.(Boolean(v))}
                  aria-label={`Toggle ${row.label}`}
                />
              </div>
            )
          })}
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="sm:mr-auto">Cancel</Button>
          <Button onClick={handleSave}>Save preferences</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
